import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils";
import ContentMarkdown from "../../ContentMarkdown";
import { EVIDENCE_ANSWER_SEGMENT_CLASS } from "../../../domains/gpt/answer";
import { JuisciGradeBadge } from "./JuisciGradeBadge";
import { PopoverPanel, usePopoverReveal } from "./InfoPopover";
import {
  type EvidenceGraphView,
  type EvidenceSourceView,
} from "../../../domains/gpt/hooks/useEvidenceGptChat";
import { citationIdentity } from "../../../domains/gpt/citation";

// Order the cited source ids by first appearance across the segments — the same
// numbering the server's `renderCitedEvidenceAnswer` uses, so `[n]` is stable.
export function citedOrder(
  segments: NonNullable<EvidenceGraphView["answer"]>
): string[] {
  const seen: string[] = [];
  for (const segment of segments)
    for (const id of segment.sourceIds) if (!seen.includes(id)) seen.push(id);
  return seen;
}

// The full source numbering every evidence surface shares: prose-cited sources
// first (the [n] the answer shows), then the sources only edges cite, in edge
// order. One definition so the answer chips, the Sources tab rows, and the
// Evidence tab's source links can never disagree on a number.
export function evidenceSourceOrder(answer: EvidenceGraphView): string[] {
  const order = citedOrder(answer.answer === null ? [] : answer.answer);
  for (const edge of answer.edges)
    if (!order.includes(edge.sourceId)) order.push(edge.sourceId);
  return order;
}

// The gate-verified verbatim passage(s) each source was cited for — what the
// citation sheet shows. Deduped, in edge order. Shared across the answer, the
// Sources tab rows, and the Evidence tab, so a source opens with the same
// quotes wherever it is tapped.
export function verbatimQuotesBySource(
  edges: EvidenceGraphView["edges"]
): Map<string, string[]> {
  const quotes = new Map<string, string[]>();
  for (const edge of edges) {
    const existing = quotes.get(edge.sourceId);
    if (existing === undefined) quotes.set(edge.sourceId, [edge.verbatim]);
    else if (!existing.includes(edge.verbatim)) existing.push(edge.verbatim);
  }
  return quotes;
}

// One shared badge look for a source number — a rounded SQUARE sized to the
// text line (a real tap target, not a tiny exponent). Used identically inline
// (a button, after the sentence it supports), at the head of each row in the
// end-of-answer source list, and on the details panel's source rows.
export const CITATION_BADGE_CLASS =
  "inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-lg bg-ds-mint-100 px-1 text-xs font-semibold text-ds-mint-800";

// An inline citation chip. Tap opens the citation sheet; hovering with a mouse
// previews the source (title, first author et al., year) in the shared dark
// panel, so a reader can check a citation without leaving the sentence. The
// reveal is `usePopoverReveal`'s hover half only (tap has its own job here),
// placed by measurement so it never clips off-screen.
function CitationBadge({
  n,
  source,
  onClick,
}: {
  n: number;
  source: EvidenceSourceView;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const reveal = usePopoverReveal("left");
  const identity = citationIdentity(source);
  return (
    <span
      ref={reveal.anchorRef}
      {...reveal.anchorHandlers}
      className="relative inline-flex align-middle"
    >
      <button
        type="button"
        onClick={onClick}
        className={cn(CITATION_BADGE_CLASS, "mx-0.5 hover:bg-ds-mint-200")}
      >
        {n}
      </button>
      {reveal.visible && (
        <PopoverPanel reveal={reveal} className="pointer-events-none">
          <span className="block font-semibold text-white">
            {source.title === null ? t("Untitled source") : source.title}
          </span>
          {identity !== "" && <span className="block">{identity}</span>}
        </PopoverPanel>
      )}
    </span>
  );
}

/**
 * One Evidence GPT answer: the synthesized segments rendered as flowing prose
 * with inline, tappable `[n]` citation chips and the overall JuisciGrade badge.
 * Every chip opens the citation sheet for its source; the numbered source list
 * lives in `EvidenceSourcesList` (the Sources tab on the unified surface, below
 * the answer on the legacy standalone one). When no claim survived the fidelity
 * gate the answer is `null` — there is nothing faithful to render, so say so.
 */
export function EvidenceAnswer({
  answer,
  onOpenSource,
}: {
  answer: EvidenceGraphView;
  onOpenSource: (
    source: EvidenceSourceView,
    citationNumber: number,
    quotes: readonly string[]
  ) => void;
}) {
  const { t } = useTranslation();

  if (answer.answer === null) {
    return (
      <p className="text-sm text-ds-slate-600">
        {t(
          "No claim could be grounded in a verifiable source for this question."
        )}
      </p>
    );
  }

  const order = citedOrder(answer.answer);
  const numberById = new Map(order.map((id, index) => [id, index + 1]));
  const sourceById = new Map(
    answer.sources.map((source) => [source.id, source])
  );
  const quotesBySourceId = verbatimQuotesBySource(answer.edges);
  function quotesFor(sourceId: string): readonly string[] {
    const quotes = quotesBySourceId.get(sourceId);
    return quotes === undefined ? [] : quotes;
  }

  return (
    <div className="flex flex-col gap-3">
      {answer.juisciGrade !== null && (
        <JuisciGradeBadge grade={answer.juisciGrade} />
      )}

      {/* One block per segment, rendered as markdown like a General answer
        (the synthesis contract: a segment is one paragraph or a SINGLE bullet
        item). A bullet segment's marker is drawn HERE, not by markdown: a
        markdown `- item` becomes a block ul/li, which would push the citation
        chips onto their own line between bullets — so the leading "- " is
        stripped and the disc rendered as a flex column, keeping the body an
        inline paragraph the chips can trail. */}
      <div className="flex flex-col gap-2">
        {answer.answer.map((segment, segmentIndex) => {
          const bulletMatch = /^-\s+/.exec(segment.text);
          const body =
            bulletMatch === null
              ? segment.text
              : segment.text.slice(bulletMatch[0].length);
          const chips = segment.sourceIds.map((id) => {
            const n = numberById.get(id);
            const source = sourceById.get(id);
            if (n === undefined || source === undefined) return null;
            return (
              <CitationBadge
                key={id}
                n={n}
                source={source}
                onClick={() => {
                  onOpenSource(source, n, quotesFor(source.id));
                }}
              />
            );
          });
          return (
            <div
              key={segmentIndex}
              className={bulletMatch === null ? undefined : "flex gap-2 ps-1"}
            >
              {bulletMatch !== null && (
                <span className="select-none text-ds-mint-800">•</span>
              )}
              <span>
                <ContentMarkdown
                  className={EVIDENCE_ANSWER_SEGMENT_CLASS}
                  math={false}
                  linkVariant="anchor"
                >
                  {body}
                </ContentMarkdown>
                {chips}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
